<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { messageStamp } from 'shared/helpers/timeHelper';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const emit = defineEmits(['edit']);

const store = useStore();
const { t } = useI18n();
const expanded = ref(false);

const scheduledMessages = computed(() =>
  store.getters['scheduledMessages/getScheduledMessages'](props.conversationId)
);

const count = computed(() => scheduledMessages.value.length);

const fetch = id => {
  if (!id) return;
  store
    .dispatch('scheduledMessages/get', { conversationId: id })
    .catch(() => {});
};

watch(
  () => props.conversationId,
  id => {
    expanded.value = false;
    fetch(id);
  },
  { immediate: true }
);

const formatTime = isoString =>
  messageStamp(new Date(isoString).getTime() / 1000, 'LLL d, h:mm a');

const onCancel = async scheduledMessage => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('CONVERSATION.SCHEDULE_MESSAGE.CANCEL_CONFIRM')))
    return;
  try {
    await store.dispatch('scheduledMessages/delete', {
      conversationId: props.conversationId,
      scheduledMessageId: scheduledMessage.id,
    });
    useAlert(t('CONVERSATION.SCHEDULE_MESSAGE.CANCELLED'));
  } catch (error) {
    useAlert(t('CONVERSATION.SCHEDULE_MESSAGE.ERROR'));
  }
};
</script>

<template>
  <div v-show="count > 0" class="px-4 pt-2">
    <button
      class="flex items-center gap-1 text-xs font-medium text-n-slate-11 hover:text-n-slate-12"
      @click="expanded = !expanded"
    >
      <span class="i-ph-clock" />
      {{ $t('CONVERSATION.SCHEDULE_MESSAGE.COUNT', { count }) }}
      <span :class="expanded ? 'i-ph-caret-up' : 'i-ph-caret-down'" />
    </button>
    <ul v-if="expanded" class="flex flex-col gap-2 mt-2">
      <li
        v-for="sm in scheduledMessages"
        :key="sm.id"
        class="flex items-start gap-2 rounded-lg border border-n-weak bg-n-alpha-black2 p-2"
      >
        <div class="flex flex-col flex-1 min-w-0">
          <span class="text-xs text-n-slate-11">{{
            formatTime(sm.scheduled_at)
          }}</span>
          <span class="text-sm text-n-slate-12 truncate">{{ sm.content }}</span>
        </div>
        <div class="flex gap-1 flex-shrink-0">
          <NextButton
            v-tooltip.top="$t('CONVERSATION.SCHEDULE_MESSAGE.EDIT')"
            icon="i-ph-pencil-simple"
            slate
            faded
            xs
            @click="emit('edit', sm)"
          />
          <NextButton
            v-tooltip.top="$t('CONVERSATION.SCHEDULE_MESSAGE.CANCEL')"
            icon="i-ph-trash"
            ruby
            faded
            xs
            @click="onCancel(sm)"
          />
        </div>
      </li>
    </ul>
  </div>
</template>
